import Validator from 'validatorjs';
import qs from 'qs';
import en from 'validatorjs/src/lang/en';

// TODO: FIXME load dynamically
import customMessages from '@/i18n/en-us/custom-validator-messages';

/**
 * Is `key` an own key of `form`? Plain objects only — a form held as an array
 * (the team-member settings screen holds one) names nothing by key.
 *
 * @param {*} form
 * @param {string} key
 * @returns {boolean}
 */
function ownsKey(form, key) {
    return form !== null && typeof form === 'object' && !Array.isArray(form) && Object.prototype.hasOwnProperty.call(form, key);
}

/**
 * Add the own keys of `form`, and the first segment of each, to `roots`.
 *
 * @param {*} form
 * @param {Set<string>} roots
 * @returns {void}
 */
function collectRoots(form, roots) {
    if (form === null || typeof form !== 'object' || Array.isArray(form)) {
        return;
    }

    Object.keys(form).forEach((key) => roots.add(String(key).split('.')[0]));
}

/**
 * Every field name the components CURRENTLY ON SCREEN below `vm` (and `vm`
 * itself) speak for: the keys of each one's `rules()`, the first segment of
 * each of those, and the own keys of each one's `form`.
 *
 * The subtree is walked rather than the calling component alone because a form
 * is routinely assembled from children — the team-member screen mounts a
 * jurisdiction-details form that binds its own keys, and a key that child
 * renders is not unbound just because the page above it has never heard of it.
 *
 * @param {object} vm  A mounted component's public instance.
 * @returns {{ keys: Set<string>, roots: Set<string> }}
 */
function fieldsOnScreen(vm) {
    const keys = new Set();
    const roots = new Set();

    const visitVNode = (vnode) => {
        if (vnode === null || typeof vnode !== 'object') {
            return;
        }

        if (vnode.component) {
            visitComponent(vnode.component);
            return;
        }

        if (vnode.suspense) {
            visitVNode(vnode.suspense.activeBranch);
        }

        if (Array.isArray(vnode.children)) {
            vnode.children.forEach(visitVNode);
        }
    };

    const visitComponent = (instance) => {
        if (!instance) {
            return;
        }

        const proxy = instance.proxy;

        if (proxy) {
            if (typeof proxy.$options.rules === 'function') {
                try {
                    Object.keys(proxy.$options.rules.call(proxy)).forEach((key) => {
                        keys.add(key);
                        roots.add(String(key).split('.')[0]);
                    });
                } catch (error) {}
            }

            collectRoots(proxy.form, roots);
        }

        visitVNode(instance.subTree);
    };

    visitComponent(vm.$);

    return { keys, roots };
}

/**
 * Does anything on screen render `key`?
 *
 * A dotted key is a LITERAL key of `serverSideErrors`, never a path into it —
 * `jurisdictionDetails.0.value` is one string. So a dotted key is answered on
 * its collection: if some form on screen binds `jurisdictionDetails`, the row
 * the server is complaining about is on screen and the message lands, whatever
 * index it carries. Treating a dotted key as unbound because the whole literal
 * string is not a key of the form would report EVERY jurisdiction and settings
 * refusal — the very messages forms-03 wired up — a second time.
 *
 * @param {string} key
 * @param {*} form
 * @param {{ keys: Set<string>, roots: Set<string> }} fields
 * @returns {boolean}
 */
function isRendered(key, form, fields) {
    if (fields.keys.has(key)) {
        return true;
    }

    const root = String(key).split('.')[0];

    return fields.roots.has(root) || ownsKey(form, root);
}

/**
 * Does `key` match one of the caller's explicitly rendered keys?
 *
 * A rendered key may name one segment as `*` to stand for any single segment —
 * `items.*.return` covers `items.0.return`, `items.7.return` — because a form
 * that renders one slot per row has no fixed key to list. Segment counts must
 * agree; `*` never swallows a deeper path.
 *
 * @param {string} key
 * @param {string[]} renderedKeys
 * @returns {boolean}
 */
function isExplicitlyRendered(key, renderedKeys) {
    const segments = String(key).split('.');

    return renderedKeys.some((rendered) => {
        const pattern = String(rendered).split('.');

        return pattern.length === segments.length && pattern.every((segment, i) => segment === '*' || segment === segments[i]);
    });
}

export default {
    install(app) {
        // Set messages first before registering custom validators
        Validator.setMessages('en', en);

        // Register the nullable validator - allows field to be null/empty
        // When nullable, the field passes validation if empty/null
        Validator.register(
            'nullable',
            function (value) {
                // nullable always passes - it just allows empty values
                return true;
            },
            'nullable|attribute=:attribute'
        );

        app.mixin({
            data() {
                return {
                    fieldChanged: {},
                    clientErrors: {},
                    serverSideError: false,
                    serverSideErrors: {},
                };
            },

            attributes() {
                return {};
            },

            watch: {
                serverErrors: function () {
                    this.$setServerSideErrors(this.serverErrors);
                },

                serverSideErrors: function () {
                    // Hide client side errors and only show server side errors
                    this.clientErrors = {};

                    if (this.$options.rules) {
                        const validationRules = this.$options.rules();

                        Object.keys(validationRules).forEach((key) => {
                            this.fieldChanged[key] = false;
                        });
                    }
                },
            },

            beforeMount() {
                // {
                //     name: 'custom_name';
                // }

                // Configure global formatter.
                Validator.setAttributeFormatter(function (attribute) {
                    return attribute
                        .replace('_id', '')
                        .replace(/_/g, ' ')
                        .replace(/[0-9]+/g, '')
                        .replace(/[\.]+/g, ' ');
                });

                Validator.setMessages('en', en);

                if (this.$options.rules) {
                    const validationRules = this.$options.rules();

                    this.clientErrors = {};
                    this.fieldChanged = {};

                    Object.keys(validationRules).forEach((key) => {
                        this.clientErrors[key] = null;
                        this.fieldChanged[key] = false;

                        this.$watch('form.' + key, (value) => {
                            // Mark field as having been modified
                            this.fieldChanged[key] = true;

                            // Check if the field is valid based on the rules
                            this.$checkField(key, value, validationRules[key]);
                        });
                    });
                }
            },

            methods: {
                $register: function (name, callbackFn, errorMessage) {
                    Validator.register(name, callbackFn, errorMessage);
                },

                $checkField: function (field, value, validation) {
                    let data = {},
                        rule = {};

                    data[field] = value;
                    rule[field] = validation;

                    try {
                        let validator = new Validator(data, rule, customMessages);

                        if (this.$options.attributes()) {
                            validator.setAttributeNames(this.$options.attributes());
                        }

                        if (validator.fails()) {
                            this.clientErrors[field] = this.$getI18nMessage(validator.errors.get(field));
                            return;
                        }

                        if (validator.passes()) {
                            this.clientErrors[field] = null;
                            return;
                        }
                    } catch (error) {}
                },

                $formValidated: function (form = null, rules = null) {
                    // Reset all client and server errors
                    this.serverSideErrors = {};

                    const validationRules = rules ? rules : this.$options.rules();

                    form = form ? form : this.form;

                    let validation = new Validator(form, validationRules, customMessages);

                    if (this.$options.attributes()) {
                        validation.setAttributeNames(this.$options.attributes());
                    }

                    if (validation.fails()) {
                        // console.log('validator.errors');
                        // console.log(validation.errors.all());
                        this.$nextTick(() => {
                            Object.entries(validation.errors.all()).forEach((error) => {
                                this.clientErrors[error[0]] = this.$getI18nMessage(error[1]);
                            });
                        });

                        return false;
                    }

                    return true;
                },

                $hasClientError: function (key) {
                    if (typeof this.clientErrors[key] === 'undefined' || this.clientErrors[key] == null) {
                        return false;
                    }

                    return true;
                },

                $hasClientErrors: function () {
                    let errorsPresent = false;
                    Object.keys(this.clientErrors).some((key) => {
                        if (this.$hasClientError(key)) {
                            return (errorsPresent = true);
                        }
                    });

                    return errorsPresent;
                },

                $firstClientError: function (key) {
                    if (this.$hasClientError(key)) {
                        return this.clientErrors[key][0];
                    }

                    return null;
                },

                $clearClientErrors: function () {
                    this.clientErrors = {};
                    if (this.$options.rules) {
                        const validationRules = this.$options.rules();
                        Object.keys(validationRules).forEach((key) => {
                            this.clientErrors[key] = null;
                            this.fieldChanged[key] = false;
                        });
                    }
                },

                $parseServerSideErrors: function (errors) {
                    let parsedErrors = {};

                    Object.keys(errors).some((key) => {
                        parsedErrors[key] = {};
                        Object.keys(errors[key]).some((n) => {
                            const entry = errors[key][n];

                            // Two 422 shapes are live wire contracts (evidence run 19,
                            // D1): encoded getRules() failures carry {message, attributes}
                            // translation keys, while BaseProcess::failValidation() pushes
                            // an already-translated prose STRING. Feeding a string's
                            // undefined ['message'] to $t made vue-i18n throw mid-catch
                            // and the refusal rendered nothing — prose renders directly,
                            // only the encoded shape is translated.
                            parsedErrors[key][n] =
                                typeof entry === 'string' ? entry : this.$t(entry['message'], entry['attributes']);
                        });
                    });

                    return parsedErrors;
                },

                $setServerSideErrors: function (errors) {
                    // Hide client side errors and only show server side errors
                    this.clientErrors = {};

                    if (this.$options.rules) {
                        const validationRules = this.$options.rules();

                        Object.keys(validationRules).forEach((key) => {
                            this.fieldChanged[key] = false;
                        });
                    }
                    this.serverSideErrors = {};
                    // Set server side errors
                    Object.keys(errors).some((key) => {
                        this.serverSideErrors[key] = errors[key];
                    });
                },

                $hasServerSideError: function (key) {
                    if (typeof this.serverSideErrors[key] === 'undefined' || this.serverSideErrors[key] == null) {
                        return false;
                    }

                    return true;
                },

                $hasServerSideErrors: function () {
                    let errorsPresent = false;
                    Object.keys(this.serverSideErrors).some((key) => {
                        if (this.$hasServerSideError(key)) {
                            return (errorsPresent = true);
                        }
                    });

                    return errorsPresent;
                },

                $firstServerSideError: function (key) {
                    if (this.$hasServerSideError(key)) {
                        return this.serverSideErrors[key][0];
                    }

                    return null;
                },

                /**
                 * The parsed server-side errors that NOTHING on this screen renders.
                 *
                 * A 422 naming a field no form binds used to arrive and disappear: the
                 * error was mapped onto `serverErrors`, handed to a form, and dropped
                 * because no input was listening for that key. `forms-03` found three
                 * live instances of exactly that, none of which announced itself in any
                 * way. The convention was set on the bank-import dialogues
                 * (`frontend/tests/unit/bank-import-dialog-unbound-422.spec.js`): a key
                 * the active form does not bind gets said out loud instead.
                 *
                 * Callers use the result to choose what to put in the toast — the
                 * server's own sentence when it would otherwise be said nowhere, and the
                 * generic `error.422.message` when the form is already showing it inline.
                 * Either way exactly one message is shown, so a bound key never
                 * double-reports.
                 *
                 * Additive: no existing helper's behaviour changes.
                 *
                 * @param {object} errors  Output of `$parseServerSideErrors()`.
                 * @param {object|null} form  The submitted form; defaults to `this.form`.
                 * @returns {Array<{ key: string, message: * }>}
                 */
                $unboundServerSideErrors: function (errors, form = null) {
                    const refused = errors ?? {};
                    const submitted = form ?? this.form ?? {};
                    const fields = fieldsOnScreen(this);

                    return Object.keys(refused)
                        .filter((key) => !isRendered(key, submitted, fields))
                        .map((key) => ({ key: key, message: refused[key] ? refused[key][0] : null }));
                },

                /**
                 * Parse a 422's errors, bind every key for the caller's form, and hand
                 * back whatever that form would otherwise swallow.
                 *
                 * The sibling `$unboundServerSideErrors` decides "rendered" by
                 * walking the screen, and treats every own key of an on-screen `form`
                 * as rendered. That is wrong for a page whose form object owns keys
                 * nothing renders — a route-sourced `invoice_id`, a hidden
                 * `combinable_parent_inventory_id` — so the tenant-scoped exists
                 * refusal on exactly those keys would be mapped inline onto nothing
                 * and the page would sit silent. This helper takes the rendered set
                 * from the caller instead, explicitly, and the caller keeps that list
                 * (and its fold rule) next to the form it describes.
                 *
                 * - Each key is bound under `fold(key)`, so a per-index server key
                 *   (`additional_taxes.3`) can land on the single slot a form renders
                 *   (`additional_taxes`). `bound` is what the caller assigns to
                 *   `serverErrors`; its values are the parser's, unchanged.
                 * - A rendered key may name one segment as `*` (`items.*.return`), so
                 *   a form that renders per row lists its slot once and needs no fold.
                 * - `unbound` is the server's own sentence for every key the rendered
                 *   set does not cover, in server order. A key that carries no
                 *   message contributes the translated `fallbackKey` instead
                 *   (`error.422.message` unless the caller names its own copy, as the
                 *   bank-import dialogue does per tab), and a 422 with no keys at
                 *   all yields `[fallback]`. A rendered key lands inline and is never
                 *   listed, so nothing is reported twice.
                 * - This helper never flashes (ruled 2026-09-17): a toast sits under a
                 *   modal backdrop and every 422 stays with the form, so the caller
                 *   renders `unbound` in a form-level panel above its fields.
                 *
                 * Additive: no existing helper's behaviour changes.
                 *
                 * @param {object|undefined} errors  The raw `errors` object of a 422 body.
                 * @param {string[]} renderedKeys  Every key the caller's form renders an error for.
                 * @param {(key: string) => string} [fold]  Server key → the key the form renders it under.
                 * @param {string} [fallbackKey]  Translation key used when no server sentence is available.
                 * @returns {{ bound: object, unbound: string[] }}  The parsed errors keyed by their folded names, and the sentences nothing renders.
                 */
                $bindServerSideErrors: function (errors, renderedKeys, fold = (key) => key, fallbackKey = 'error.422.message') {
                    const parsedErrors = this.$parseServerSideErrors(errors ?? {});
                    const keys = Object.keys(parsedErrors);
                    const fallback = this.$t(fallbackKey);
                    const bound = {};

                    keys.forEach((key) => {
                        bound[fold(key)] = parsedErrors[key];
                    });

                    const unbound = keys.filter((key) => !isExplicitlyRendered(fold(key), renderedKeys)).map((key) => parsedErrors[key][0] ?? fallback);

                    if (keys.length === 0) {
                        unbound.push(fallback);
                    }

                    return { bound, unbound };
                },

                $getI18nMessage: function (error) {
                    let result = error.toString().split('|');
                    let rule = 'validation.' + result[0];
                    let params = qs.parse(result[1]);

                    // the message field expects an array to be returned
                    return [this.$t(rule, params)];
                },

                $getValidationRules: function () {
                    return this.$options.rules();
                },
            },
        });
    },
};
